import {reducerStoreKey} from './reducer';
import {actionCreators} from 'Core/store/reducers';
import {ioJsonFetchAction} from 'Core/store/actions/io';
import {isSuccessful} from 'Core/helpers/io';
import {getArray} from 'Core/helpers/data';
import {hideLoading, showLoading} from 'Core/helpers/loading';
import * as segmentItemDataMap from './dataMap/listItem';
import * as conditionDataMap from 'DataMaps/conditions/condition';
import * as itemFilterDataMap from 'Components/advanced/ImportCustomersFromList/dataMap/filter';
import {get} from 'lodash';

/**
 * Reset customers segment Redux state to initial state
 * @return {(function(*))|*}
 */
export const resetCustomersSegmentAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].reset());
};

/**
 * Fetch customers segment
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} projectId - ID of the project (customers are assigned to projects).
 * @param {ConditionListItemDataObject[]} conditions - Customer segment conditions.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. Sort fields are defined by the API.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {string} [clientId=''] - DB ID of the client (used by super admin).
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const fetchCustomersSegmentAction = (
	abortCallback, projectId, conditions, filter = null, pageNo = 1, perPage, sortBy, sortDir, clientId = ''
) => dispatch => {
	return ioJsonFetchAction(
		abortCallback,
		'defaultAuthorizedApi',
		'campaign/select-customer-segment',
		'',
		filter,
		null,
		pageNo,
		perPage,
		sortBy,
		sortDir,
		{
			clientId,
			projectId,
			conditions: getArray(conditions).map((c, idx) => ({...conditionDataMap.output(c), ordinal: idx + 1}))
		}
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => {
			if (isSuccessful(responseData)) {
				return ({
					...responseData,
					filter: itemFilterDataMap.input(get(responseData, 'filter')),
					data: getArray(responseData, 'data').map(i => segmentItemDataMap.input(i))
				});
			}
			return undefined;
		});
};

/**
 * Load customers segment into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} projectId - ID of the project (customers are assigned to projects).
 * @param {ConditionListItemDataObject[]} conditions - Customer segment conditions.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. Sort fields are defined by the API.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {boolean} [loadingOverlay=true] - Flag that specifies if loading overlay will be rendered.
 * @param {string} [clientId] - DB ID of the client (used by super admin).
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const loadCustomersSegmentAction = (
	abortCallback, projectId, conditions, filter = null, pageNo = 1, perPage, sortBy, sortDir, loadingOverlay = true, 
	clientId
) => dispatch => {
	const loading = (loadingOverlay === true ? showLoading("#customers-segment-preview-table") : null);
	return fetchCustomersSegmentAction(
		abortCallback, projectId, conditions, filter, pageNo, perPage, sortBy, sortDir, clientId
	)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setCustomersSegmentData(responseData));
			if (loading !== null) hideLoading(loading);
			return responseData;
		});
};

/**
 * Set customer segment filter in Redux store
 *
 * @param {Object} filter - Customer list filter to set.
 * @return {(function(*): void)|*}
 */
export const setCustomersSegmentFilterAction = filter => dispatch => {
	dispatch(actionCreators[reducerStoreKey].setCustomersSegmentFilter(filter));
};